import * as Sentry from '@sentry/vue'
import Vue from 'vue'
import configs from '@/config'
import store from '@/vuex/store'
import SystemConnection, { CONNECTION_STATUS } from '@/utils/system/SystemConnection'
import QuizStatuses from '@/constant/QuizStatuses'
import sentry from '@/services/sentry'
import mixpanelUtils from '@/utils/mixpanel.utils'

let resource = null

const actions = {
  createNewSlide: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide/create/`,
  },
  createGeneratedSlide: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide/create-generated-slide/`,
  },
  getSelectedParticipantFields: {
    method: 'GET',
    url: `${configs.baseUrl}/api/slide/{slideId}/selected-participant-fields/`,
  },
  updateSelectedParticipantFields: {
    method: 'PATCH',
    url: `${configs.baseUrl}/api/slide/{slideId}/selected-participant-fields/`,
  },
  updateSlideById: {
    method: 'PUT',
    url: `${configs.baseUrl}/api/slide/update/{slideId}`,
  },
  patchSlideById: {
    method: 'PATCH',
    url: `${configs.baseUrl}/api/slide/{slideId}`,
  },
  patchCategoriesBySlideId: {
    method: 'PATCH',
    url: `${configs.baseUrl}/api/slide/{slideId}/matching-question-options`,
  },
  updateNextSlideById: {
    method: 'PUT',
    url: `${configs.baseUrl}/api/presentation/update-next-slide/{presentationId}`,
  },
  slideScaleQuestionById: {
    method: 'GET',
    url: `${configs.baseUrl}/api/slide/scale-result/{slideId}`,
  },
  patchSlides: {
    method: 'PATCH',
    url: `${configs.baseUrl}/api/slide/`,
  },

  duplicateSlides: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide/duplicate-slides/`,
  },
  clearSlides: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide/clear-slides/`,
  },
  clearSlidesViaProxy: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/live/slide/clear-slides/`,
  },
  clearSlidesViaProxyNewKafka: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/${configs.LiveProxyRootPath}/slide/clear-slides/`,
  },
  spinTheWheel: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide/spinner-wheel/{slideId}/spin`,
  },
  stopTheWheel: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide/spinner-wheel/{slideId}/stop`,
  },
  updateTheWheel: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide/spinner-wheel/{slideId}/update`,
  },
  matchPairsResult: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide/{slideId}/get-result/`,
  },
  duplicateOtherPresentation: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide/duplicate-other-presentation/`,
  },
  getPublicSlideTemplates: {
    method: 'GET',
    url: `${configs.baseUrl}/api/slide/public-templates`,
  },
  publishContentSlideTemplate: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide/public-templates`,
  },
  getSlideById: {
    method: 'GET',
    url: `${configs.baseUrl}/api/slide/{slideId}`,
  },
  listSlidesDetail: {
    method: 'GET',
    url: `${configs.baseUrl}/api/slide/by-ids`,
  },
  reSignedImages: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide/resigned-image`,
  },
  wordCloudSmartGrouping: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide/{slideId}/word-cloud-smart-grouping`,
  },
  getSlideByGoogle: {
    method: 'GET',
    url: `${configs.baseUrl}/api/google-slide/slide-by-google/{slideId}`,
  },

  setIsAIIndicatorVisible: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide/{slideId}/slide:touch`,
  },

  getListSlidesByPresentationId: {
    method: 'GET',
    url: `${configs.baseUrl}/api/presentation/audience-presentation-slide-list/{presentationId}`,
  },
  updateSlideByIdThroughProxy: {
    method: 'PUT',
    url: `${configs.baseUrl}/api/live/slide/update/{slideId}`,
  },
  getCanvasBlocks: {
    method: 'GET',
    url: `${configs.baseUrl}/api/slide/canvas-blocks`,
    auth: false,
    headers: {
      authorization: null,
    },
  },
  getCanvasBlocksNoCached: {
    method: 'GET',
    url: `${configs.baseUrl}/api/slide/canvas-blocks`,
    auth: false,
    headers: {
      authorization: null,
      'Cache-Control': 'no-cache',
    },
  },
  sortSlides: {
    method: 'PUT',
    url: `${configs.api.sortSlide}{presentationId}`,
  },
  getCurrentTimestamp: {
    method: 'GET',
    url: `${configs.baseUrl}/api/quiz/get-time-stamp/`,
  },
  getCurrentSlideIdByOldSlideId: {
    method: 'GET',
    url: `${configs.baseUrl}/api/presenting-sessions/slides/{presentationId}/latest-related?slideIds={slideId}`,
  },
  getSlideAttributesByPresentationId: {
    method: 'GET',
    url: `${configs.baseUrl}/api/v2/slides/attributes?presentationId={presentationId}&attributeKeys={attributeKeys}`,
  },
  upsertSlideAttribute: {
    method: 'POST',
    url: `${configs.baseUrl}/api/v2/slides/{slideId}/attributes`,
  },
  bulkUpsertSlideAttributes: {
    method: 'PATCH',
    url: `${configs.baseUrl}/api/v2/slides/attributes`,
  },
  getAttributes: {
    method: 'GET',
    url: `${configs.baseUrl}/api/v2/slides/attributes`,
  },
  getPinnedSlideTypes: {
    method: 'GET',
    url: `${configs.baseUrl}/api/slide/pinned-slide-type`,
  },
  upsertPinnedSlideTypes: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide/pinned-slide-type`,
  },
}

const initResource = () => {
  resource = Vue.resource('', {}, actions)
}

const createNewSlide = (params) => {
  return resource.createNewSlide(params)
}

const createGeneratedSlide = (params) => {
  return resource.createGeneratedSlide(params)
}

const ActiveSlideState = (params) => {
  const obj = {
    id: params.id,
    socketId: params.socketId,
    quizStatus: params.quizStatus,
    quizTimestamp: params.quizTimestamp,
    questionCount: params.questionCount,
    questionIndex: params.questionIndex,
    isNextSlide: params.isNextSlide,
    timestamp: new Date().getTime(),
  }
  if (params.from) {
    obj.actionFrom = params.from
  }
  return obj
}

const syncActiveSlideState = async (sessionId, params) => {
  try {
    if (params.quizStatus === QuizStatuses.QUESTION_SCREEN) {
      const resp = await resource.getCurrentTimestamp()
      if (resp && resp.body && resp.body.timeStamp) {
        params.quizTimestamp = [resp.body.timeStamp]
      }
    }
  } catch (error) {
    sentry.captureException(error)
  }

  // send tracking data to help debugging nextback and quizstatus
  mixpanelUtils.trackForLoginUser('syncActiveSlideState', { presentation_id: sessionId, ...params })
  return store.dispatch('kvSync/createKeyValueAction', {
    path: `/activeSlide/${sessionId}`,
    end_parts: '',
    type: 'string',
    value: JSON.stringify(ActiveSlideState(params)),
  })
}

const updateSlideById = async (slideId, params) => {
  try {
    if (params.quizStatus) {
      const presentationId = store.state.presentation.id
      syncActiveSlideState(presentationId, {
        id: slideId,
        ...params,
      })
    }

    const res = await resource.updateSlideById({ slideId }, params)
    SystemConnection.detectGeneralConnection(CONNECTION_STATUS.GOOD)
    return res
  } catch (error) {
    if (store.getters['featureToggles/canUseNetworkIndicatorV2']) {
      SystemConnection.detectGeneralConnection(CONNECTION_STATUS.ERROR)
    } else {
      SystemConnection.detectGeneralConnection(CONNECTION_STATUS.UNSTABLE)
    }
    return { body: { error: error.message } }
  }
}

const slideScaleQuestionById = (slideId, params) => {
  return resource.slideScaleQuestionById({ slideId }, params)
}

const getSlideById = (slideId, params) => {
  return resource.getSlideById({ slideId }, params)
}

const patchSlideById = (slideId, params) => {
  return resource.patchSlideById({ slideId }, params)
}

const patchCategoriesBySlideId = (slideId, params) => {
  return resource.patchCategoriesBySlideId({ slideId }, params)
}

const updateNextSlideById = async (presentationId, params) => {
  try {
    if (store.getters['featureToggles/canUseTransactionalSlideCacheUpdate']) {
      params.transactionalSlideUpdate = true
    }
    params.isNextSlide = true
    syncActiveSlideState(presentationId, {
      id: params.activeSlide,
      ...params,
    })
    const res = await resource.updateNextSlideById({ presentationId }, params)
    SystemConnection.detectGeneralConnection(CONNECTION_STATUS.GOOD)
    return res
  } catch (error) {
    if (store.getters['featureToggles/canUseNetworkIndicatorV2']) {
      SystemConnection.detectGeneralConnection(CONNECTION_STATUS.ERROR)
    } else {
      SystemConnection.detectGeneralConnection(CONNECTION_STATUS.UNSTABLE)
    }
    try {
      Sentry.captureException(new Error('Request Presentation Next Slide Error'), {
        extra: {
          params,
          presentation: store.state.presentation,
          featureFlag: store.getters['liveStats/canUseNewChangeSlideMechanism'],
          presenting: store.getters.isPresenting,
          presentationId,
          error,
        },
      })
    } catch (e) {
      //
    }
    return { body: { error: error.message } }
  }
}

const updateSlides = async (params, source) => {
  if (source) {
    // diagnostic call-source tag; vue-resource turns params with no URL template into a query string (?source=)
    return resource.patchSlides({ source }, params)
  }
  return resource.patchSlides(params)
}

const duplicateSlides = async (params) => {
  return resource.duplicateSlides(params)
}

const clearSlides = async (params) => {
  if (store.getters['featureToggles/canUseTransactionalSlideCacheUpdate']) {
    params.transactionalSlideUpdate = true
  }
  if (store.getters['liveStatsV2/canUseLiveStatsForQuizV2']) {
    return resource.clearSlidesViaProxy(params)
  }
  return resource.clearSlides(params)
}

const spinTheWheel = async (slideId, params) => {
  return resource.spinTheWheel({ slideId }, params)
}

const stopTheWheel = async (slideId, params) => {
  return resource.stopTheWheel({ slideId }, params)
}

const updateTheWheel = async (slideId, params) => {
  return resource.updateTheWheel({ slideId }, params)
}

const matchPairsResult = (slideId, params) => {
  return resource.matchPairsResult({ slideId }, params)
}

const duplicateOtherPresentation = (params) => {
  return resource.duplicateOtherPresentation(params)
}

const getPublicSlideTemplates = async () => {
  const res = await resource.getPublicSlideTemplates()
  return res.body
}

const publishContentSlideTemplate = async (params) => {
  const res = await resource.publishContentSlideTemplate(params)
  return res.body
}

const listSlidesDetail = async ({ ids, presentationId }) => {
  const res = await resource.listSlidesDetail({ ids, presentationId })
  return res.body
}

const reSignedImages = async (params) => {
  const res = await resource.reSignedImages(params)
  return res.body
}

const wordCloudSmartGrouping = async (slideId, params) => {
  const res = await resource.wordCloudSmartGrouping({ slideId }, params)
  return res.body
}

const getSlideByGoogle = (slideId) => {
  return resource.getSlideByGoogle({ slideId })
}

const setIsAIIndicatorVisible = async (slideId) => {
  const res = await resource.setIsAIIndicatorVisible({ slideId }, {})
  return res.body
}

const createNewGoogleSlide = (params) => {
  return resource.createNewGoogleSlide(params)
}

const getListSlidesByPresentationId = (presentationId) => {
  return resource.getListSlidesByPresentationId({ presentationId })
}

const getCanvasBlocks = (slide) => {
  if (store.getters['featureToggles/isCacheCanvasBlocks']) {
    return resource.getCanvasBlocks({ slideId: slide.id, updatedAt: slide.updatedAt })
  }
  return resource.getCanvasBlocksNoCached({ slideId: slide.id, updatedAt: slide.updatedAt })
}

const sortSlides = async (presentationId, payload) => {
  const res = await resource.sortSlides({ presentationId }, payload)
  return res.body
}

const updateSelectedParticipantFields = async (slideId, params, queryParams = {}) => {
  const res = await resource.updateSelectedParticipantFields({ slideId, ...queryParams }, params)
  return res.body
}

const getSelectedParticipantFields = async (slideId) => {
  const res = await resource.getSelectedParticipantFields({ slideId })
  return res.body
}

const getCurrentSlideIdByOldSlideId = async ({ presentationId, slideId }) => {
  const res = await resource.getCurrentSlideIdByOldSlideId({ presentationId, slideId })
  return res.body
}

const getSlideAttributesByPresentationId = async ({ presentationId, attributeKeys }) => {
  const res = await resource.getSlideAttributesByPresentationId({ presentationId, attributeKeys })
  return res.body
}
const upsertSlideAttribute = async (slideId, params) => {
  const res = await resource.upsertSlideAttribute({ slideId }, params)
  return res.body
}

const bulkUpsertSlideAttributes = async ({ presentationId, slides }) => {
  const res = await resource.bulkUpsertSlideAttributes({}, { presentationId, slides })
  return res.body
}

const getAttributesBySlideId = async ({ slideId, ...queryParams }) => {
  const res = await resource.getAttributes({ slideIds: slideId, ...queryParams })
  return res.body
}

const getPinnedSlideTypes = async () => {
  const res = await resource.getPinnedSlideTypes()
  return res.body
}

const upsertPinnedSlideTypes = async (slugs) => {
  const res = await resource.upsertPinnedSlideTypes({ slugs })
  // vue-resource's interceptor chain can convert XHR network errors into resolved
  // responses with status 0 / ok=false. Throw explicitly so callers can catch.
  if (!res.ok) throw Object.assign(new Error(`HTTP ${res.status}`), { response: res })
  return res.body
}

export default {
  initResource,
  createNewSlide,
  createGeneratedSlide,
  patchSlideById,
  updateNextSlideById,
  updateSlideById,
  slideScaleQuestionById,
  updateSlides,
  duplicateSlides,
  clearSlides,
  spinTheWheel,
  stopTheWheel,
  updateTheWheel,
  matchPairsResult,
  duplicateOtherPresentation,
  getPublicSlideTemplates,
  publishContentSlideTemplate,
  getSlideById,
  listSlidesDetail,
  reSignedImages,
  wordCloudSmartGrouping,
  getSlideByGoogle,
  patchCategoriesBySlideId,
  setIsAIIndicatorVisible,
  createNewGoogleSlide,
  getListSlidesByPresentationId,
  getCanvasBlocks,
  sortSlides,
  updateSelectedParticipantFields,
  getSelectedParticipantFields,
  getCurrentSlideIdByOldSlideId,
  syncActiveSlideState,
  getSlideAttributesByPresentationId,
  upsertSlideAttribute,
  bulkUpsertSlideAttributes,
  getAttributesBySlideId,
  getPinnedSlideTypes,
  upsertPinnedSlideTypes,
}
